import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSupportUnreadByConversation } from '../services/chatApi';
import { supportKeys } from './queryKeys';
/**
 * Short on purpose: the counts are only worth showing if they are fresh when someone looks. This
 * just folds a burst (focus + drawer open in the same moment) into one read.
 */
const LIST_STALE_MS = 5000;
const NO_UNREAD = {};
/**
 * Unread support replies per conversation, for the conversation list.
 *
 * The orchestrator's `GET /api/chat` returns `support_unread_count` for every conversation it
 * lists, so the drawer needs no per-conversation call. It is paged, and the read follows its cursor
 * until every conversation the drawer shows has been seen (bounded; see
 * `SUPPORT_UNREAD_LIST_MAX_PAGES`). The list is not live, so it is re-read when the list opens
 * (`enabled` turning true, and the drawer expanding, which invalidates this query) and whenever
 * the window regains focus; the ACTIVE conversation is taken from the session hook instead (it
 * hears replies as they arrive and zeroes the count the moment they are read) and overrides
 * whatever the list last said about it. There is no live push for the OTHER conversations: no
 * user-level stream exists, so a reply there shows on the next open or focus.
 *
 * The list read goes to the orchestrator, so it waits for the AI token (`hasAiToken`) rather than
 * firing on open, ahead of auth. The dots are best-effort and degrade to none.
 */
export function useConversationSupportUnread({ activeConversationId, activeUnreadCount, enabled, shownConversationIds, hasAiToken }) {
    var _a;
    // Read at fetch time, not part of the key: a changed list must not drop the dots already known.
    const shownIdsRef = useRef(shownConversationIds);
    shownIdsRef.current = shownConversationIds;
    const list = useQuery({
        queryKey: supportKeys.unreadList(),
        queryFn: () => getSupportUnreadByConversation(shownIdsRef.current),
        enabled: enabled && hasAiToken && shownConversationIds.length > 0,
        staleTime: LIST_STALE_MS,
        retry: false,
        refetchOnWindowFocus: true
    });
    const listed = (_a = list.data) !== null && _a !== void 0 ? _a : NO_UNREAD;
    return useMemo(() => {
        const unread = Object.assign({}, listed);
        if (activeConversationId !== null) {
            if (activeUnreadCount > 0)
                unread[activeConversationId] = activeUnreadCount;
            else
                delete unread[activeConversationId];
        }
        return unread;
    }, [listed, activeConversationId, activeUnreadCount]);
}
